/**
 * The popup a payout row opens on the Owners page's payouts view (COL-617):
 * which rooms the payout is still waiting on, and which are paid.
 *
 * Lines are the tenancies the row's "n of m tenants unpaid" counts
 * (OwnerPayoutGroup.rooms, one per tenancy since COL-625), unpaid first. Once part of the month was paid out
 * (an early payout left a remainder, COL-483) the popup says so, and the paid
 * rooms split on that payout: the ones paid since go into the payout still to
 * make, the ones paid before it went out with it and sit collapsed below —
 * see payoutRoomSections. The full owner profile, which the row used to open,
 * stays one click away.
 *
 * COL-625: one line per tenancy, so a room let twice in the month (a move-out
 * and a move-in) shows each tenant on a line of their own; a moved-out
 * tenant's unpaid rent is written off and says so on their line.
 *
 * COL-621: a partly paid rent that is really a kickback (lowered by hand, at
 * coliviq's cost) can be marked as one right here; it then counts as paid in
 * full for the owner and moves to the paid rooms with a "Kickback" tag.
 */
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Gift,
  History,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  payoutRoomSections,
  type PayoutRoomDetail,
  type PayoutTenancyDetail,
} from "@/lib/owner-payouts";
import {
  RentKickbackStaleError,
  useMarkRentKickback,
} from "@/hooks/v2/useMarkRentKickback";
import { RentKickbackClosedError } from "@/lib/rent-kickback-guard";
import { cn } from "@/lib/utils";
import {
  apartmentCount,
  eur,
  monthName,
  StatusPill,
  type PayoutViewItem,
  type Translate,
} from "./owner-payouts-parts";

/** "21 Oct" for a payment time, in the admin's language. */
function shortDate(iso: string, language: string | undefined): string {
  return new Date(iso).toLocaleDateString(
    language === "de" ? "de-DE" : "en-GB",
    { day: "numeric", month: "short" }
  );
}

function roomName(room: PayoutRoomDetail, t: Translate): string {
  const title = room.roomTitle ?? t("admin.owners.payoutDetail.room", "Room");
  return room.apartmentLabel ? `${room.apartmentLabel} · ${title}` : title;
}

function SummaryTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "muted" | "warning";
}) {
  return (
    <div className="min-w-0 rounded-lg bg-muted/50 px-3 py-2.5">
      <div className="truncate text-xs text-muted-foreground">{label}</div>
      <div
        className={cn(
          "text-lg font-semibold tabular-nums",
          tone === "muted" && "text-muted-foreground",
          tone === "warning" && "text-amber-700"
        )}
      >
        {value}
      </div>
    </div>
  );
}

function SectionHeading({
  icon,
  label,
  count,
  tone,
  invoiceNo,
  t,
}: {
  icon: ReactNode;
  label: string;
  count: string;
  tone: "warning" | "success";
  invoiceNo?: string | null;
  t: Translate;
}) {
  return (
    <div className="mt-5 mb-1 flex items-center gap-2 text-sm font-medium">
      {icon}
      <span>{label}</span>
      <span
        className={cn(
          "rounded-full px-2 py-px text-xs font-normal tabular-nums",
          tone === "warning"
            ? "bg-amber-50 text-amber-700"
            : "bg-emerald-50 text-emerald-700"
        )}
      >
        {count}
      </span>
      {invoiceNo && (
        <span className="ml-auto text-xs font-normal text-muted-foreground">
          {t("admin.owners.payoutDetail.inInvoice", "in {invoice}").replace(
            "{invoice}",
            invoiceNo
          )}
        </span>
      )}
    </div>
  );
}

/** "Kickback €100.00" under a tenant whose counted rent coliviq partly funds. */
function KickbackTag({ eurValue, t }: { eurValue: number; t: Translate }) {
  return (
    <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-px text-xs font-medium text-violet-700">
      <Gift className="h-3 w-3" aria-hidden="true" />
      {t("admin.owners.payoutDetail.kickbackTag", "Kickback {amount}").replace(
        "{amount}",
        eur(eurValue)
      )}
    </span>
  );
}

/**
 * COL-621: "Mark as kickback" under a partly paid tenancy whose shortfall is a
 * rent lowered by hand (PayoutTenancyDetail.kickbackCandidate). One click opens
 * a one-line confirm that says what changes; confirming writes the row's gross
 * rent, and the refetched payout moves the room to the paid ones.
 */
function KickbackAction({
  tenancy,
  t,
}: {
  tenancy: PayoutTenancyDetail;
  t: Translate;
}) {
  const [confirming, setConfirming] = useState(false);
  const mark = useMarkRentKickback();
  const candidate = tenancy.kickbackCandidate;
  if (!candidate) return null;
  const amount = eur((candidate.grossCents - candidate.amountCents) / 100);

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="mt-0.5 inline-flex items-center gap-1 rounded text-xs font-medium text-violet-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Gift className="h-3 w-3" aria-hidden="true" />
        {t("admin.owners.payoutDetail.markKickback", "Mark as kickback")}
      </button>
    );
  }

  const confirm = () =>
    mark.mutate(
      {
        paymentId: candidate.paymentId,
        bookingId: tenancy.bookingId,
        amountCents: candidate.amountCents,
        grossCents: candidate.grossCents,
      },
      {
        onSuccess: () => {
          setConfirming(false);
          toast.success(
            t(
              "admin.owners.payoutDetail.kickbackSaved",
              "Marked as kickback. The owner is paid the full rent."
            )
          );
        },
        onError: (e) => {
          setConfirming(false);
          toast.error(
            e instanceof RentKickbackClosedError
              ? t(
                  "admin.owners.payoutDetail.kickbackClosed",
                  "This month is already paid out or invoiced to the owner. A kickback can no longer be recorded."
                )
              : e instanceof RentKickbackStaleError
                ? t(
                    "admin.owners.payoutDetail.kickbackStale",
                    "This rent changed in the meantime. Nothing was saved — check it again."
                  )
                : t(
                    "admin.owners.payoutDetail.kickbackFailed",
                    "Could not mark this rent as a kickback."
                  )
          );
        },
      }
    );

  return (
    <span className="mt-1.5 block rounded-md border border-violet-200 bg-violet-50/60 px-2.5 py-2 text-xs text-violet-900">
      <span className="block">
        {t(
          "admin.owners.payoutDetail.kickbackConfirm",
          "coliviq covers the missing {amount}. The owner is paid as if the full rent came in."
        ).replace("{amount}", amount)}
      </span>
      {/* Wraps: in German the two buttons are wider than the tenant column. */}
      <span className="mt-1.5 flex flex-wrap gap-1.5">
        <Button
          size="sm"
          className="h-7 px-2.5 text-xs"
          onClick={confirm}
          disabled={mark.isPending}
        >
          {t(
            "admin.owners.payoutDetail.kickbackConfirmAction",
            "Mark as kickback"
          )}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2.5 text-xs"
          onClick={() => setConfirming(false)}
          disabled={mark.isPending}
        >
          {t("admin.owners.payoutDetail.kickbackCancel", "Cancel")}
        </Button>
      </span>
    </span>
  );
}

/**
 * One section's rooms. Unpaid rooms show what came in and what is missing;
 * paid ones what came in and when. A room with two tenancies this month (one
 * moving out, one in) lists both tenants.
 */
function RoomList({
  rooms,
  unpaid,
  propertyId,
  onNavigate,
  t,
  language,
}: {
  rooms: PayoutRoomDetail[];
  unpaid: boolean;
  propertyId: string;
  onNavigate: () => void;
  t: Translate;
  language: string | undefined;
}) {
  const cols =
    "grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1.4fr)_88px_88px]";
  return (
    <div className="text-sm">
      <div
        className={cn(
          cols,
          "hidden py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground sm:grid"
        )}
      >
        <span>{t("admin.owners.payoutDetail.col.room", "Room")}</span>
        <span>{t("admin.owners.payoutDetail.col.tenant", "Tenant")}</span>
        <span className="text-right">
          {t("admin.owners.payoutDetail.col.paid", "Paid")}
        </span>
        <span className="text-right">
          {unpaid
            ? t("admin.owners.payoutDetail.col.missing", "Missing")
            : t("admin.owners.payoutDetail.col.paidOn", "Paid on")}
        </span>
      </div>
      <ul>
        {rooms.map((room) => (
          <li
            key={room.lineId}
            className={cn(cols, "items-start border-t border-border/60 py-2")}
          >
            <span className="min-w-0 break-words font-medium sm:font-normal">
              {roomName(room, t)}
            </span>
            <span className="col-start-1 row-start-2 min-w-0 sm:col-start-auto sm:row-start-auto">
              {room.tenancies.map((tenancy) => (
                <span key={tenancy.bookingId} className="block min-w-0">
                  <Link
                    to={`/admin/properties/${propertyId}/rooms/${room.roomId}/tenants/${tenancy.bookingId}`}
                    onClick={onNavigate}
                    className="block truncate text-primary hover:underline"
                  >
                    {tenancy.tenantName ??
                      t("admin.owners.payoutDetail.tenant", "Tenant")}
                  </Link>
                  {unpaid && (
                    <span className="block text-xs text-muted-foreground">
                      {tenancy.collectedEur > 0
                        ? t(
                            "admin.owners.payoutDetail.partlyPaid",
                            "Partly paid"
                          )
                        : t(
                            "admin.owners.payoutDetail.nothingPaid",
                            "Nothing paid yet"
                          )}
                    </span>
                  )}
                  {unpaid && <KickbackAction tenancy={tenancy} t={t} />}
                  {tenancy.kickbackEur > 0 && (
                    <KickbackTag eurValue={tenancy.kickbackEur} t={t} />
                  )}
                  {tenancy.writtenOffEur > 0 && (
                    <span className="block text-xs text-muted-foreground">
                      {t(
                        "admin.owners.payoutDetail.writtenOff",
                        "Moved out · {amount} written off"
                      ).replace("{amount}", eur(tenancy.writtenOffEur))}
                    </span>
                  )}
                </span>
              ))}
            </span>
            <span className="row-span-2 text-right tabular-nums sm:row-span-1">
              {eur(room.collectedEur)}
              {/* Phones have no column headings: the second figure says what
                  it is. */}
              <span
                className={cn(
                  "block sm:hidden",
                  unpaid ? "text-amber-700" : "text-xs text-muted-foreground"
                )}
              >
                {unpaid
                  ? `${t("admin.owners.payoutDetail.col.missing", "Missing")} ${eur(room.shortfallEur)}`
                  : room.lastPaidAt && shortDate(room.lastPaidAt, language)}
              </span>
            </span>
            <span
              className={cn(
                "hidden text-right tabular-nums sm:block",
                unpaid
                  ? "font-medium text-amber-700"
                  : "text-xs text-muted-foreground"
              )}
            >
              {unpaid
                ? eur(room.shortfallEur)
                : room.lastPaidAt
                  ? shortDate(room.lastPaidAt, language)
                  : "—"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function OwnerPayoutDetailDialog({
  item,
  onClose,
  onOpenOwner,
}: {
  /** The payout to show; null keeps the popup closed. */
  item: PayoutViewItem | null;
  onClose: () => void;
  onOpenOwner: (row: PayoutViewItem["row"]) => void;
}) {
  const { t, language } = useLanguage();
  const [showPaidBefore, setShowPaidBefore] = useState(false);

  const handleOpenChange = (open: boolean) => {
    if (open) return;
    setShowPaidBefore(false);
    onClose();
  };

  if (!item) return <Dialog open={false} onOpenChange={handleOpenChange} />;

  const { group } = item;
  const sections = payoutRoomSections(group);
  const prior = group.priorPayout;
  const month = monthName(group.periodMonth, language, true);
  const apts = apartmentCount(group);
  const raw =
    group.expectedEur > 0 ? (group.collectedEur / group.expectedEur) * 100 : 0;
  // Same rounding as the row: a pending payout never reads as a full 100%.
  const pct =
    group.status === "pending"
      ? Math.min(99, Math.floor(raw))
      : Math.min(100, Math.round(raw));
  const roomsTotal = group.rooms.length;

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader className="space-y-1 text-left">
          <div className="flex flex-wrap items-center gap-2 pr-6">
            <DialogTitle className="text-lg">
              {item.row.lessor?.name ?? "—"}
            </DialogTitle>
            <StatusPill status={group.status} t={t} />
            {prior && (
              <span className="inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                {t("admin.owners.payoutDetail.remainderTag", "Remainder")}
              </span>
            )}
          </div>
          <DialogDescription>
            {item.propertyTitle ??
              t("admin.owners.payouts.property", "Property")}
            {apts > 0 && (
              <>
                {" · "}
                <span className="tabular-nums">{apts}</span>{" "}
                {apts === 1
                  ? t("admin.owners.payouts.apartment", "apartment")
                  : t("admin.owners.payouts.apartments", "apartments")}
              </>
            )}
            {" · "}
            {month}
          </DialogDescription>
        </DialogHeader>

        {prior && (
          <div className="flex items-start gap-2 rounded-lg bg-sky-50 px-3 py-2.5 text-sm text-sky-900">
            <History className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              {group.openRemainder
                ? t(
                    "admin.owners.payoutDetail.remainderNote",
                    "{month} was already paid out once with {previous}. This remainder, {invoice}, pays out the rent collected after that."
                  )
                    .replace("{month}", month)
                    .replace("{previous}", prior.invoiceNo ?? "—")
                    .replace("{invoice}", group.openRemainder.invoiceNo ?? "—")
                : t(
                    "admin.owners.payoutDetail.partlyPaidOutNote",
                    "Part of {month} was already paid out with {previous}. The rest is paid out once the missing rent is in."
                  )
                    .replace("{month}", month)
                    .replace("{previous}", prior.invoiceNo ?? "—")}
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
          {prior ? (
            <>
              <SummaryTile
                label={t(
                  "admin.owners.payouts.alreadyPaidOut",
                  "Already paid out"
                )}
                value={eur(group.paidOutEur ?? 0)}
                tone="muted"
              />
              <SummaryTile
                label={t(
                  "admin.owners.payoutDetail.collectedSince",
                  "Collected since"
                )}
                value={eur(group.payoutRentEur)}
              />
            </>
          ) : (
            <>
              <SummaryTile
                label={t(
                  "admin.owners.payouts.rentCollected",
                  "Rent collected"
                )}
                value={`${pct}%`}
              />
              <SummaryTile
                label={t(
                  "admin.owners.payoutDetail.stillOutstanding",
                  "Still outstanding"
                )}
                value={eur(group.shortfallEur)}
                tone={group.shortfallEur > 0 ? "warning" : undefined}
              />
            </>
          )}
          <SummaryTile
            label={t("admin.owners.payouts.liveNetPayout", "Live Net Payout")}
            value={eur(group.livePayoutEur)}
          />
        </div>

        {sections.unpaid.length > 0 && (
          <>
            <SectionHeading
              icon={
                <AlertCircle
                  className="h-4 w-4 text-amber-600"
                  aria-hidden="true"
                />
              }
              label={t("admin.owners.payoutDetail.unpaid", "Unpaid")}
              count={`${sections.unpaid.length} ${t("admin.owners.payouts.of", "of")} ${roomsTotal}`}
              tone="warning"
              t={t}
            />
            <RoomList
              rooms={sections.unpaid}
              unpaid
              propertyId={group.propertyId}
              onNavigate={onClose}
              t={t}
              language={language}
            />
          </>
        )}
        {sections.unpaid.length === 0 && group.ledgerOutstanding && (
          // Every tenancy reads paid, but the invoice's own ledger still has
          // an open rent row (COL-483) — the row says the same.
          <p className="mt-4 text-sm text-amber-700">
            {t(
              "admin.owners.payouts.ledgerRowOpenMonth",
              "A rent payment for {month} is still open"
            ).replace("{month}", month)}
          </p>
        )}

        {sections.paid.length > 0 && (
          <>
            <SectionHeading
              icon={
                <CheckCircle2
                  className="h-4 w-4 text-emerald-600"
                  aria-hidden="true"
                />
              }
              label={
                sections.paidBefore
                  ? t(
                      "admin.owners.payoutDetail.paidSince",
                      "Paid since last payout"
                    )
                  : t("admin.owners.payoutDetail.paid", "Paid")
              }
              count={String(sections.paid.length)}
              tone="success"
              invoiceNo={
                sections.paidBefore ? group.openRemainder?.invoiceNo : null
              }
              t={t}
            />
            <RoomList
              rooms={sections.paid}
              unpaid={false}
              propertyId={group.propertyId}
              onNavigate={onClose}
              t={t}
              language={language}
            />
          </>
        )}

        {sections.paidBefore && sections.paidBefore.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => setShowPaidBefore((v) => !v)}
              aria-expanded={showPaidBefore}
              className="mt-5 mb-1 flex w-full items-center gap-2 rounded-md text-left text-sm font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronRight
                className={cn(
                  "h-4 w-4 transition-transform",
                  showPaidBefore && "rotate-90"
                )}
                aria-hidden="true"
              />
              <span>
                {t("admin.owners.payouts.alreadyPaidOut", "Already paid out")}
              </span>
              <span className="rounded-full bg-muted px-2 py-px text-xs font-normal tabular-nums">
                {sections.paidBefore.length}
              </span>
              {prior?.invoiceNo && (
                <span className="ml-auto text-xs font-normal">
                  {t(
                    "admin.owners.payoutDetail.inInvoice",
                    "in {invoice}"
                  ).replace("{invoice}", prior.invoiceNo)}
                </span>
              )}
            </button>
            {showPaidBefore && (
              <RoomList
                rooms={sections.paidBefore}
                unpaid={false}
                propertyId={group.propertyId}
                onNavigate={onClose}
                t={t}
                language={language}
              />
            )}
          </>
        )}

        <DialogFooter className="mt-2 border-t pt-4">
          <Button
            variant="outline"
            onClick={() => {
              const row = item.row;
              handleOpenChange(false);
              onOpenOwner(row);
            }}
          >
            {t("admin.owners.payoutDetail.openOwner", "Open owner profile")}
            <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
